import React, { useState } from 'react';
import { useTranslation } from '../context/LanguageContext';
import { apiService } from '../services/api';
import { ForgotPasswordRequest } from '../types/api';
import './Login.css'; // Reuse login styles
import reforged1024 from '../assets/reforged-1024x330.png';
import Turnstile from './Turnstile';
import LanguageSelector from './LanguageSelector';

interface ForgotPasswordProps {
  onBackToLogin: () => void;
}

const ForgotPassword: React.FC<ForgotPasswordProps> = ({ onBackToLogin }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<ForgotPasswordRequest>({
    email: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [message, setMessage] = useState<string>('');
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

  const handleTurnstileVerify = (token: string) => {
    setTurnstileToken(token);
    // Clear any turnstile-related errors
    if (errors.some(error => error.includes('security verification'))) {
      setErrors(errors.filter(error => !error.includes('security verification')));
    }
  };

  const handleTurnstileError = () => {
    setTurnstileToken(null);
    setErrors(prev => {
      const filteredErrors = prev.filter(error => !error.includes('security verification'));
      // Only add the error if it's not already there
      if (!filteredErrors.some(error => error.includes('Security verification failed'))) {
        return [...filteredErrors, 'Security verification failed. Please try again.'];
      }
      return filteredErrors;
    });
  };

  const handleTurnstileExpire = () => {
    setTurnstileToken(null);
    setErrors(prev => [...prev.filter(error => !error.includes('security verification')), 'Security verification expired. Please verify again.']);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    // Clear errors when user starts typing
    if (errors.length > 0) setErrors([]);
    if (message) setMessage('');
  };

  const validateForm = (): boolean => {
    const newErrors: string[] = [];

    if (!formData.email.trim()) {
      newErrors.push('Email is required');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.push('Invalid email format');
    }

    if (!turnstileToken) {
      newErrors.push('Please complete the security verification');
    }

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors([]);
    setMessage('');

    try {
      const requestData = {
        ...formData,
        turnstileToken: turnstileToken || undefined
      };
      const response = await apiService.forgotPassword(requestData);
      
      if (response.success) {
        setMessage(t('passwordResetSent'));
        // Reset form
        setFormData({ email: '' });
        setTurnstileToken(null);
      } else {
        if (response.errors && response.errors.length > 0) {
          setErrors(response.errors);
        } else {
          setErrors([response.message || 'Request failed']);
        }
      }
    } catch (err: any) {
      console.error('Forgot password error:', err);
      const errorData = err.response?.data;
      if (errorData?.errors && errorData.errors.length > 0) {
        setErrors(errorData.errors);
      } else {
        setErrors([
          errorData?.message || 
          err.message || 
          'An error occurred while processing your request'
        ]);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const isFormValid = formData.email.trim() && 
                     /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email) &&
                     turnstileToken;

  return (
    <div className="login-container">
      <div className="login-logo">
        <img src={reforged1024} alt="Reforged Training Zone" className="logo-image" />
      </div>
      <div className="login-card">
        <div className="login-header">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <span></span>
            <LanguageSelector />
          </div>
          <h2>{t('resetPassword')}</h2>
          <p>{t('enterEmailToReceiveReset')}</p>
        </div>
        
        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="email">{t('emailAddress')}</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
              disabled={isLoading}
              placeholder={t('email')}
            />
          </div>

          <div className="form-group">
            <label>{t('securityVerification')} *</label>
            <Turnstile
              onVerify={handleTurnstileVerify}
              onError={handleTurnstileError}
              onExpire={handleTurnstileExpire}
              theme="auto"
              size="normal"
            />
          </div>
          
          {errors.length > 0 && (
            <div className="error-message">
              {errors.map((error, index) => (
                <div key={index}>{error}</div>
              ))}
            </div>
          )}

          {message && (
            <div className="success-message">
              {message}
            </div>
          )}
          
          <button
            type="submit"
            disabled={!isFormValid || isLoading}
            className="login-button"
          >
            {isLoading ? t('sending') : t('sendResetInstructions')}
          </button>
        </form>
        
        <div className="login-footer">
          <div className="login-links">
            <button 
              type="button" 
              className="link-button" 
              onClick={onBackToLogin}
              disabled={isLoading}
            >
              {t('backToLogin')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword; 